// Sources:
// * https://richardwillis.info/blog/generate-a-type-script-http-client-from-an-open-api-spec-in-dot-net-5
// * https://openapi-generator.tech/docs/generators/typescript-axios/

/*
* Cart Service API
* Uses OpenAPI generator to generate the API client and uses axios to make requests.
* Includes the logic for obtaining a bearer token initially and retrying the request if the token is expired.
* Makes use of item storage to store the bearer token to reduce the number of requests to the identity service.
**/

import {isDevelopmentBuild, isPreProd} from "../utils/constants";
import {webService} from "./web";
import tokenService from "../utils/jwtTokenHandler";
import {CartApi, Configuration, ConfigurationParameters, GPAWebDataTypesResourceType} from './openApi/cartService';
import axios, {AxiosHeaders, AxiosInstance} from "axios";
import Cookies from "js-cookie";
import {getHostedSiteId, identity} from "../utils/helpers";
import * as Sentry from "@sentry/react";

/**
 * Creates an instance of the CartApi class. Uses the Axios instance to make requests.
 * @param {AxiosInstance} instance
 * @param instance
 * @constructor
 */
function CreateApiClient(instance: AxiosInstance) {
    const getServiceEndpoint = () => {
        // return "https://localhost:44322";
        if (isDevelopmentBuild) return `https://hntotd574ungun6jc6kzpzi5540odhdr.lambda-url.us-east-1.on.aws`;
        if (isPreProd) return `https://rqvgv3g52undanqei3nh4gcwie0geaoa.lambda-url.us-east-1.on.aws`;
        return 'https://p65s6ne4gablyrcryl4qo66nxa0jnysb.lambda-url.us-east-1.on.aws';
    };

    const BASE_URL = getServiceEndpoint();

    const configParams: ConfigurationParameters = {
        basePath: BASE_URL,
        // Obtain the initial bearer token from the web service.
        accessToken: resolveToken
    };

    const apiConfig = new Configuration(configParams)

    return {
        cartApi: new CartApi(apiConfig, undefined, instance)
    };
}

/**
 * Resolves the bearer token. If the token is not in the item storage, calls the web service to obtain the token.
 */
async function resolveToken() {
    return tokenService.get() ?? await webService.identity.token();
}

/**
 * Creates an Axios instance with a bearer token interceptor that will retry the request if the token is expired.
 * Calls the web to obtain the bearer token.
 * @returns {AxiosInstance}
 * @constructor
 */
const CreateAxiosInstanceWithBearerTokenRetry = (): AxiosInstance => {
    const AUTHORIZATION = "Authorization";

    const DEFAULT_HEADERS = {
        "Content-Type": "application/json"
    };

    const instance = axios.create({
        headers: DEFAULT_HEADERS,
    });

    instance.interceptors.request.use(
        async (axiosRequestConfig) => {
            if (!axiosRequestConfig.headers)
                // https://github.com/axios/axios/issues/5573#issuecomment-1696877449
                // Axios update broken assigned headers, new up the headers for now.
                axiosRequestConfig.headers = new AxiosHeaders(DEFAULT_HEADERS)

            if (axiosRequestConfig.headers[AUTHORIZATION]) {
                return axiosRequestConfig;
            }

            const token = await resolveToken();

            if (token)
                axiosRequestConfig.headers[AUTHORIZATION] = token;

            return axiosRequestConfig;
        },
        error => Promise.reject(error)
    );

    instance.interceptors.response.use(
        response => {
            return response;
        },
        async error => {
            const originalRequest = error.config;

            if (!error.response || error.response.status !== 401 || error.response.state || originalRequest._retry) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                const token = await webService.identity.token()

                return instance({
                    ...originalRequest,
                    headers: {
                        ...originalRequest.headers,
                        Authorization: token
                    },
                    _retry: true
                });
            } catch (_error) {
                return Promise.reject(_error);
            }
        }
    );
    return instance;
};

class CartService {
    // Unused since the CartApi class and Axios instance know how to obtain the token.
    private authorization: string = "";

    constructor(private client: ReturnType<typeof CreateApiClient>) {
    }

    /**
     * Creates the cookie for the given cartId.
     * Note: This cookie allows javascript to read the cookie.
     * @param {string} cartId The cartId to store in the cookie.
     * @throws {Error} If the cookie cannot be created.
     * */
    createCookie(cartId: string) {
        const ThirtyDays = 30 * 24 * 60 * 60 * 1000;
        Cookies.set("GWHS", `scid=${cartId}`, {
            path: "/", // set the cookie for the entire site
            expires: new Date(Date.now() + ThirtyDays), // expires in 30 days
            httponly: false, // allow the cookie to be read by javascript
            secure: true // require https, set to true if your site is on https
        });
    }

    deleteCookie() {
        Cookies.remove("GWHS");
    }

    /**
     * Gets the cartId from the cookie.
     * @returns {string} The cartId.
     * @throws {Error} If the cookie is not found.
     * */
    getCartId(): undefined | string {
        const cookie = Cookies.get("GWHS");
        if (cookie) {
            const [key, value] = cookie.split("=");
            return value;
        }
        return undefined;
    }

    /**
     * Creates a cart for the customer.
     */
    async createCartAsync(currencyCode: string, catalogId: number) {
        await this.ensureClaims()
        const {accountId, isInternalUser} = identity();
        const request = {
            accountId: accountId,
            hsId: getHostedSiteId(catalogId),
            catalogId: catalogId,
            currencyCode: currencyCode,
            internal: isInternalUser,
            allowShipPartial: false,
        };
        return await this.client.cartApi.cartCreateAsync(this.authorization, request)
    }

    /**
     * Gets the cart for the customer.
     */
    async getCartAsync(cartId: string, omitAddresses: boolean = false, omitGroups: boolean = false) {
        return await this.client.cartApi.cartGetAsync(cartId, this.authorization, omitAddresses, omitGroups, {
            validateStatus: (status: number) => {
                if (status >= 200 && status <= 299) {
                    return true
                }
                return status === 404;
            }
        })
    }

    /**
     * @method ensureClaims
     * @description Ensures that the token is present and valid.
     * If not, it will request a new token from the identity service.
     * The claims are needed before submitting requests since the accountId must be known ahead of time.
     * @returns {Promise<void>}
     * @throws {Error} If the token cannot be retrieved from the identity service
     * @private
     */
    private async ensureClaims(): Promise<void> {
        const storedToken = tokenService.get();
        if (storedToken === null) {
            await webService.identity.token()
        }
    }

    async getOrCreateCart(currencyCode: string, catalogId: number) {
        // Attempt to get the cartId from the cookie
        const cartId = this.getCartId();

        // If the cartId is not found, create a new cart
        if (!cartId) return await this.createCart(currencyCode, catalogId);

        // Verify that cart id corresponds to a MUTABLE cart
        const exists = await this.getCartExistsAsync(cartId);

        // If the cart does not exist, create a new cart
        if (!exists) return await this.createCart(currencyCode, catalogId);

        // Pull back the cart to verify access
        const getCartResponse = await this.getCartAsync(cartId, true, true);

        // If the response is successful and the cart id is valid, return the cart id
        if (getCartResponse && getCartResponse.status === 200 && getCartResponse.data.id) return cartId;

        // Otherwise, create a new cart and update the cookie
        return await this.createCart(currencyCode, catalogId);
    }

    private async createCart(currencyCode: string, catalogId: number) {
        const createCartResponse = await this.createCartAsync(currencyCode, catalogId);
        if (createCartResponse.status === 201 && createCartResponse.data.id) {
            this.createCookie(createCartResponse.data.id);
            return createCartResponse.data.id;
        }
        throw new Error('Unable to create cart');
    }

    async updateCart(cartId: string, item: CartItemType) {
        return await this.client.cartApi.cartUpdateAsync(cartId, this.authorization, [
            {
                op: "replace",
                path: "promotionCode",
                value: item.promotionCode,
            }
        ], {
            validateStatus: (status: number) => {
                return status === 200
            }
        });
    }

    async createItem(cartId: string, item: CartItemType) {
        return await this.client.cartApi.cartAddItemAsync(cartId, this.authorization, {
            productId: item.productId,
            artworkId: item.artworkId,
            width: item.width,
            height: item.height,
            quantity: item.quantity,
            notes: item.artworkNotes,
        }, {
            validateStatus: (status: number) => {
                if (status === 400) return true;
                return status >= 200 && status <= 299;
            }
        });
    }

    async createItems(cartId: string, items: CartItemType[]) {
        const mappedItems = items.map(item => {
            return {
                productId: item.productId,
                artworkId: item.artworkId,
                width: item.width,
                height: item.height,
                quantity: item.quantity,
            }
        });
        return await this.client.cartApi.cartAddItemsAsync(cartId, this.authorization, mappedItems, {
            validateStatus: (status: number) => {
                if (status === 400) return true;
                return status >= 200 && status <= 299;
            }
        });
    }

    async createItemResources(cartId: string, item: CartItemType) {
        const resourceRequests = item.resources?.map(resource => ({
            ...item,
            resourceId: resource.resourceId,
            resourcePrice: resource.resourcePrice,
            resourceType: resource.resourceType,
        }));

        return await this.client.cartApi.cartAddItemResourcesAsync(cartId, this.authorization, resourceRequests, {
            validateStatus: (status: number) => {
                if (status === 400) return true;
                return status >= 200 && status <= 299;
            }
        });
    }

    async getItemByAttributes(cartId: string, item: CartItemType) {
        const cartResponse = await this.getCartAsync(cartId);

        if (cartResponse && cartResponse.data && cartResponse.status === 200) {
            // create map of items using createKey function as the key and the item as the value
            return cartResponse.data.groups?.flatMap(group => group.items)
                .find(x =>
                    x?.group?.productId == item.productId &&
                    x?.artworkId == item.artworkId &&
                    x?.width == item.width &&
                    x?.height == item.height
                );
        }
    }

    async getCart() {
        const cartId = this.getCartId()
        if (!cartId) throw new Error('Cart cookie not found');
        return this.getCartAsync(cartId);
    }

    async getCartExistsAsync(cartId: string) {
        return await this.client.cartApi.cartExistsAsync(cartId, this.authorization)
            .then(({status, data}) => status === 200 && data)
            .catch(() => false);
    }
}

// Create the axios instance which supports bearer token retries.
const instance = CreateAxiosInstanceWithBearerTokenRetry();

// Create the API client with the axios instance to retry authentication requests.
const client = CreateApiClient(instance);

// Create the cart service with the API client.
const service = new CartService(client);

export type CartItemType = {
    quantity: number | null;
    productId: number;
    artworkId: number | null;
    artworkName: string | null;
    artworkNotes: string | null;
    width: number | null;
    height: number | null;
    resources?: CartItemResourceType[];
    promotionCode?: string;
}

export type CartItemResourceType = {
    resourceId: number;
    resourcePrice: number;
    resourceType: GPAWebDataTypesResourceType;
}

// Wrapper object over the cart service and API client.
// Supports re-generating the open api client without breaking consumers.
export const cartService = {
    createItem: async (currencyCode: string, catalogId: number, item: CartItemType) => {
        const cartId = await service.getOrCreateCart(currencyCode, catalogId);
        try {
            return await service.createItem(cartId, item);
        } catch (e) {
            logCartServiceError(e, 'createItem', {cartId, item, catalogId, currencyCode});
            throw e;
        }
    },
    createItems: async (currencyCode: string, catalogId: number, items: CartItemType[]) => {
        const cartId = await service.getOrCreateCart(currencyCode, catalogId);
        try {
            return await service.createItems(cartId, items);
        } catch (e) {
            logCartServiceError(e, 'createItems', {cartId, item: items, catalogId, currencyCode});
            throw e;
        }
    },
    createItemResources: async (item: CartItemType) => {
        const cartId = service.getCartId();
        if (!cartId) throw new Error('Cart cookie not found');
        try {
            return await service.createItemResources(cartId, item);
        } catch (e) {
            logCartServiceError(e, 'createItemResources', {cartId, item});
            throw e;
        }
    },
    applyCoupon: async (item: CartItemType) => {
        const cartId = service.getCartId();
        if (!cartId) throw new Error('Cart cookie not found');
        try {
            return await service.updateCart(cartId, item);
        } catch (e) {
            logCartServiceError(e, 'applyCoupon', {cartId, item});
            throw e;
        }
    }
}

const logCartServiceError = (e: any,
                             functionName: string,
                             payload: {
                                 cartId: string,
                                 item: CartItemType | CartItemType[],
                                 catalogId?: number,
                                 currencyCode?: string
                             }) => {
    const {claims: {aud, sub}} = identity();
    Sentry.captureException(e, {
        extra: {
            functionName,
            user: {
                aud,
                sub,
            },
            payload
        }
    });
};